import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  FormLayoutContent,
  FormLayoutFooter,
  FormLayout
} from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import { snmpUserTemplate } from 'routes/SnmpUser/templates/snmpUserTemplate';
import { adaptTemplateVisibilityMode, adaptSnmpUserTemplate } from 'routes/SnmpUser/utils/snmpUserTemplate.adapter';
import { newSnmpUser } from 'routes/SnmpUser/SnmpUser.types';
import { SnmpV3User } from 'data/snmp/snmp.types';
import * as snmpUserActions from 'data/snmp/snmp.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import SnmpUserForm, { Ref } from 'routes/SnmpUser/components/SnmpUserForm';
import { FieldUpdateHandlerParam } from 'shared/form';
import { adaptSnmpUserToCreate } from 'data/snmp/snmp.adapter';
import { getAuditDetailsToSubmit } from 'routes/SnmpUser/utils/snmpUserTemplateDataToSubmit';

type RouterParams = {
  history: string;
};

const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  snmpUserTemplateDef: adaptTemplateVisibilityMode(snmpUserTemplate, 'create')
});

const mapDispatch = {
  goToSmnpUserList: snmpUserActions.goToSmnpUserList,
  createSnmpUser: snmpUserActions.createSnmpUser
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const SnmpUserCreate = ({
  history,
  userId,
  deliveryFunctionId,
  snmpUserTemplateDef,
  userName,
  isAuditEnabled,
  goToSmnpUserList,
  createSnmpUser
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<SnmpV3User>(cloneDeep(newSnmpUser));
  const [template, setTemplate] = useState(snmpUserTemplateDef);
  const { t } = useTranslation();

  const handleFieldUpdate = ({
    name: fieldName,
    value
  }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as SnmpV3User);
    let clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value
    };

    clonedTemplate = adaptSnmpUserTemplate(
      clonedTemplate,
      fieldName,
      value
    );

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const handleClickSave = () => {
    const payload = getSnmpUserPayload();
    if (payload) {
      createSnmpUser(deliveryFunctionId, payload, history);
    }
  };

  const getSnmpUserPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const snmpUserFormData = formRef.current.getValues();

    const payload = {
      data: adaptSnmpUserToCreate(snmpUserFormData),
      auditDetails: getAuditDetailsToSubmit(
        snmpUserFormData,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE'
      )
    };
    return payload;
  };

  const handleCancel = () => goToSmnpUserList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <SnmpUserForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="SnmpUserCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('snmpUser.create.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>{renderFormPanel('')}</FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(SnmpUserCreate));
